import { CommonTabbarContext } from "@/App";
import { updateRoofSlabSection } from "@/api/building/roof-slab-section";
import { deletePrimitivesInGraphic } from "@/api/geometry/primitive";
import { CommonTabbarState } from "@/commons/components/common-tabbar";
import { TAB_TYPE } from "@/commons/enums/tab-type";
import { RoofSlabSection } from "@/commons/interface/building/roof-slab-section"
import { graphicStore } from "@/commons/store/graphic-store";
import TnInput from "@/commons/widget/input/tn-input";
import TnMessage from "@/commons/widget/message";
import classNames from "classnames";
import { PlineT, Point } from "pytha";
import { useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Vector3 } from "three";


interface Iprops {
    item: RoofSlabSection
    onSaveName: (name: string) => void
    onSaveDraw: (entity: any) => void
}

// 屋面板侧边栏-屋面板条目（编辑态）
export default function RoofSlabEditItem(props: Iprops, ref) {
    const { projectUuid } = useParams();
    const { item } = props;
    const navigate = useNavigate();
    const location = useLocation();  // 路径
    const [editing, setEditing] = useState<boolean>(false); // 当前正在编辑屋面板的名称
    const [editName, setEditName] = useState<string>(); // 当前正在编辑的屋面板的名称
    const [drawing, setDrawing] = useState<boolean>(false); // 当前正在绘制屋面板截面
    const commonTabbarContext = useContext<CommonTabbarState>(CommonTabbarContext); // tabbar上下文
    const pline = useRef<PlineT>(); // 绘制的屋面板截面
    // const addPlineCmd = useRef<AddPlineCommand>(); // 创建屋面板截面图元的命令

    useImperativeHandle(ref, () => ({
        handleEditName: handleEditName,
    }))

    useEffect(() => {
        if (!location.pathname.includes(`${TAB_TYPE.ROOF_SLAB}/${item?.uuid}`)) {
            // 路径中没有屋面板，已经跳转到别的路由页面，并没有正在绘制屋面板截面，drawing置为false
            setDrawing(false);
        }
    }, [location])

    useEffect(() => {
        handleEditName();
    }, [item])

    // 开始编辑屋面板名称
    const handleEditName = () => {
        setEditing(true);
        setEditName(item?.name);
    }

    // 输入框失焦，保存屋面板名称
    const saveName = () => {
        if (editName == null || editName === "") {
            TnMessage.error("屋面板名称不能为空");
            setEditName('');
            return;
        }
        let data = { name: editName };
        updateRoofSlabSection(item.uuid, data).then(res => {
            props.onSaveName(editName);
        }).catch(err => {
            if (err.data) {
                TnMessage.error(err.data);
            } else {
                TnMessage.error("编辑失败");
            }
        }).finally(() => {
            setEditing(false);
            setEditName('');
        })
    }

    // 开始绘制屋面板截面
    const onStartDraw = () => {
        TnMessage.info("请沿x轴方向绘制")
        // 打开绘制屋面板的标签页
        commonTabbarContext.navigate(`${TAB_TYPE.ROOF_SLAB}/${item?.uuid}`);
        setDrawing(true);
        graphicStore.extraContext.getCurrentViewEditor()?.viewCube.prepareAnimationData("pabcd");
        graphicStore.extraContext.getCurrentViewEditor().viewCube.animating = true;
    }

    const checkPline = () => {
        let entities = graphicStore.extraContext.getCurrentViewEditor().entityDict
        for (let uuid in entities) {
            let entity = entities[uuid]
            if (entity.baseType == "PlineT") {
                pline.current = entity as PlineT
                return true
            }
        }
        return false
    }

    // 保存屋面板截面
    const saveDraw = () => {
        if (!checkPline()) {
            TnMessage.warning("请完成绘制屋面板截面");
            setDrawing(false);
            return;
        }
        let plineEntity = pline.current;
        // 转至世界坐标系的原点处，存储截面世界坐标系下的几何信息
        // 计算当前截面的包围盒
        let boundingBox = plineEntity.getBoundingBox();
        let minX = boundingBox.min.x;
        let minY = boundingBox.min.y;
        let minZ = boundingBox.min.z;
        // 基点，包围盒的最小角点
        let basePoint = new Vector3(minX, minY, minZ);
        // 计算以基点为坐标原点，当前坐标系下，图元的几何信息
        let normal = graphicStore.extraContext.getCurrentViewEditor().context.ucsContext.currentUcs.axisZ;
        let axisX = graphicStore.extraContext.getCurrentViewEditor().context.ucsContext.currentUcs.axisX;
        let moveDir = basePoint.clone().negate();
        let plineEntityClone = plineEntity.clone();
        plineEntityClone.move(moveDir.clone().normalize(), moveDir.length());
        let origin = new Point(0, 0, 0);
        let worldX = new Vector3(1, 0, 0);
        let worldZ = new Vector3(0, 0, 1);
        let normalRotateAxis = normal.clone().cross(worldZ).normalize();
        if (Math.abs(normal.dot(worldZ) + 1) < 1e-6) {
            normalRotateAxis = axisX.clone();
        }
        let normalDeltaAngle = worldZ.angleTo(normal);
        plineEntityClone.rotate(origin, normalRotateAxis, normalDeltaAngle);
        let tempAxisX = axisX.clone().applyAxisAngle(normalRotateAxis, normalDeltaAngle);
        let axisXRotateAxis = tempAxisX.clone().cross(worldX).normalize();
        let axisXDeltaAngle = tempAxisX.angleTo(worldX);
        if (Math.abs(tempAxisX.dot(worldX) + 1) < 1e-6) {
            axisXRotateAxis = worldZ.clone();
        }
        plineEntityClone.rotate(origin, axisXRotateAxis, axisXDeltaAngle);


        let data = { entity: plineEntityClone.toJson() };
        updateRoofSlabSection(item.uuid, data).then(res => {
            // 删除绘制的屋面板截面图元
            deletePrimitivesInGraphic([plineEntity.uuid], projectUuid).then(res => {
                graphicStore.extraContext.getCurrentViewEditor().removeObject(plineEntity)
            }).then(res => {
                // 关闭页签
                graphicStore.extraContext.getCurrentViewEditor().listeners.signals.needRender.dispatch('redraw');
                onCloseTab(`${TAB_TYPE.ROOF_SLAB}/${item?.uuid}`, true);
                props.onSaveDraw(data.entity);
            })

        }).catch(err => {
            if (err.data) {
                TnMessage.error(err.data);
            } else {
                TnMessage.error("编辑失败");
            }
        }).finally(() => {
            setDrawing(false);
        })
    }

    // 关闭页签
    const onCloseTab = (url, active) => {
        let idx = commonTabbarContext.routes.findIndex(item => item.url === url);
        let res = commonTabbarContext.routes.filter(item => item.url != url) || [];
        commonTabbarContext.setRoutesOverwrite(res);
        if (!active) return;
        if (res.length <= 0) return;
        if (idx >= res.length) idx = res.length - 1;
        if (idx < 0) idx = 0;

        if (!!commonTabbarContext.navigate) {
            commonTabbarContext.navigate(res[idx].url);
        } else {
            navigate(-1);
        }
    }

    return (
        <div className="roof-slab-item">
            <div className={classNames({
                "name": true,
                "edit": true,
            })}>
                {editing &&
                    <TnInput
                        placeholder="输入屋面板名称"
                        value={editName}
                        onTextChange={(value) => setEditName(value)}
                        onBlur={() => saveName()}
                        autoFocus
                    />
                }
                {!editing && <div className="text">{item?.name}</div>}
            </div>
            {!drawing && <div className="draw-btn" onClick={onStartDraw}>绘制屋面板截面</div>}
            {drawing && <div className="draw-btn" onClick={saveDraw}>{"保存屋面板截面（多义线）"}</div>}
        </div>
    )
}